import {
  ActionEvents,
  CalendarEventOccurrenceTarget,
  CalendarEventRef,
} from '@griply/types';
import { getConnectionProvider, getExternalEventEditable } from '@griply/utils';
import {
  useEditCalendarEventAllowed,
  useExternalCalendarConnections,
} from 'features/external-connections';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PenEdit16, Trash16 } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useDeleteExternalCalendarEventMutation } from 'shared/hooks/queries/use-delete-external-calendar-event-mutation';
import { useExternalCalendarEventQuery } from 'shared/hooks/queries/use-external-calendar-event-query';
import { useTrackEvents } from 'shared/hooks/use-track-events';

export type ExternalEventContextMenuProps = {
  eventTarget: CalendarEventRef;
  isRecurring: boolean;
  // absent on an older backend; only an explicit `false` marks you as an invitee
  isOrganizer?: boolean;
  location: { top: number; left: number };
  // owned by the event card, which is the only place that can seed the popup
  onEdit: () => void;
  onClose: () => void;
};

export const ExternalEventContextMenu: React.FC<
  ExternalEventContextMenuProps
> = ({ eventTarget, isRecurring, isOrganizer, location, onEdit, onClose }) => {
  const { t } = useTranslation();
  const track = useTrackEvents();
  const connections = useExternalCalendarConnections();
  const { allowed: editAllowed } = useEditCalendarEventAllowed();
  const { mutate: deleteEvent } = useDeleteExternalCalendarEventMutation();

  // Same gate mobile's detail screen and the event popup apply: an invitee never sees Delete,
  // and editing calendar events is premium.
  const canDelete =
    editAllowed &&
    getExternalEventEditable(eventTarget, { connections, isOrganizer });

  // a false flag means "not known to repeat", so confirm before offering a lone single-delete
  const { details } = useExternalCalendarEventQuery(
    canDelete && !isRecurring ? eventTarget : null,
  );
  const repeats = isRecurring || !!details?.isRecurring;

  const onSelectEdit = () => {
    onEdit();
    onClose();
  };

  const deleteWithOccurrenceTarget = (
    occurrenceTarget: CalendarEventOccurrenceTarget,
  ) => {
    deleteEvent({ ...eventTarget, editScope: occurrenceTarget });
    track(ActionEvents.CalendarEventDelete, {
      provider: getConnectionProvider(connections, eventTarget.connectionId),
      is_recurring: repeats,
      edit_scope: occurrenceTarget,
    });
    onClose();
  };

  const onDelete = () =>
    deleteWithOccurrenceTarget(CalendarEventOccurrenceTarget.Single);
  const onDeleteSeries = () =>
    deleteWithOccurrenceTarget(CalendarEventOccurrenceTarget.Series);

  return (
    <PopupMenu location={location} onOutside={onClose}>
      <PopupMenuList>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onSelectEdit}
            start={<Icon icon={PenEdit16} />}
          >
            {t('context-menus.external-event.labels.edit')}
          </PopupMenuButton>
        </PopupMenuListItem>
        {canDelete && !repeats && (
          <PopupMenuListItem>
            <PopupMenuButton onClick={onDelete} start={<Icon icon={Trash16} />}>
              {t('context-menus.external-event.labels.delete')}
            </PopupMenuButton>
          </PopupMenuListItem>
        )}
        {canDelete && repeats && (
          <PopupMenuListItem>
            <PopupMenuButton onClick={onDelete} start={<Icon icon={Trash16} />}>
              {t('context-menus.external-event.labels.delete-single')}
            </PopupMenuButton>
          </PopupMenuListItem>
        )}
        {canDelete && repeats && (
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={onDeleteSeries}
              start={<Icon icon={Trash16} />}
            >
              {t('context-menus.external-event.labels.delete-series')}
            </PopupMenuButton>
          </PopupMenuListItem>
        )}
      </PopupMenuList>
    </PopupMenu>
  );
};
